import { useCommodityTypeService } from './commodityTypeService'
import { useTransactionService } from './transactionService'
import type { CommodityType } from '@/types/CommodityType'
import type { TransactionRecord } from '@/types/Transaction'

/**
 * Initialization Service - Handles loading of initial data for forms and views
 * Provides a clean way to load all necessary data for complex forms
 */
export function useInitializationService() {
  const commodityTypeService = useCommodityTypeService()
  const transactionService = useTransactionService()

  return {
    /**
     * Initialize purchase form data
     * Loads all necessary data for the purchase form in parallel
     */
    async initializePurchaseForm(transactionId: string): Promise<{
      transaction: TransactionRecord
      commodityTypes: CommodityType[]
    }> {
      try {
        // Load all necessary data in parallel
        const [transaction, commodityTypes] = await Promise.all([
          transactionService.startEditing(transactionId),
          commodityTypeService.getActiveCommodityTypes(),
        ])

        return {
          transaction,
          commodityTypes,
        }
      } catch (error) {
        console.error('Failed to initialize purchase form:', error)
        throw new Error('Failed to initialize purchase form')
      }
    },

    /**
     * Initialize transaction form data
     * Loads basic data needed for transaction forms
     */
    async initializeTransactionForm(transactionId: string): Promise<{
      transaction: TransactionRecord
      commodityTypes: CommodityType[]
    }> {
      try {
        const [transaction, commodityTypes] = await Promise.all([
          transactionService.startEditing(transactionId),
          commodityTypeService.getActiveCommodityTypes(),
        ])

        return {
          transaction,
          commodityTypes,
        }
      } catch (error) {
        console.error('Failed to initialize transaction form:', error)
        throw new Error('Failed to initialize transaction form')
      }
    },
  }
}
